/**
 * Dire le temps en mots — PUR, en `Intl` natif. Les mots viennent de la langue du navigateur
 * (`Intl.NumberFormat`, `Intl.RelativeTimeFormat`, `Intl.DateTimeFormat`), jamais d'une table
 * écrite ici : passer `locale` suffit pour l'anglais, l'espagnol, l'allemand…
 *
 *   formatHeure(ms, "Europe/Paris")           « 09:42 »
 *   formatDuree(9_900_000)                    « 2 h 45 min »
 *   formatChrono(9_909_000)                   « 2:45:09 »
 *   formatRelatif(il_y_a_12_min, maintenant)  « il y a 12 min »
 *   formatJour(ms, "Europe/Paris", "long")    « lundi 14 juillet »
 *   formatDecalage("America/Asuncion")        « UTC−3 »
 */
import { decalageDuFuseau, type Jour, type JourDeSemaine, type Mois } from "./dates";

const MINUTE = 60_000;
const HEURE = 60 * MINUTE;
const JOUR = 24 * HEURE;

const cache = new Map<string, Intl.NumberFormat | Intl.DateTimeFormat | Intl.RelativeTimeFormat>();
function memo<T extends Intl.NumberFormat | Intl.DateTimeFormat | Intl.RelativeTimeFormat>(cle: string, fabrique: () => T): T {
  let f = cache.get(cle) as T | undefined;
  if (!f) {
    f = fabrique();
    cache.set(cle, f);
  }
  return f;
}

function unite(locale: string, u: "day" | "hour" | "minute" | "second", n: number): string {
  return memo(`u|${locale}|${u}`, () => new Intl.NumberFormat(locale, { style: "unit", unit: u, unitDisplay: "short" })).format(n);
}

/** « 09:42 » — l'heure de l'instant `ms`, lue dans `fuseau` (24 h). */
export function formatHeure(ms: number, fuseau: string, locale = "fr"): string {
  return memo(
    `h|${locale}|${fuseau}`,
    () => new Intl.DateTimeFormat(locale, { timeZone: fuseau, hour: "2-digit", minute: "2-digit", hourCycle: "h23" }),
  ).format(new Date(ms));
}

export type StyleDeJour = "long" | "court" | "complet" | "numerique" | "jourMois" | "jourMoisCourt";

const OPTIONS_DE_JOUR: Record<StyleDeJour, Intl.DateTimeFormatOptions> = {
  long: { weekday: "long", day: "numeric", month: "long" },
  court: { weekday: "short", day: "numeric", month: "short" },
  complet: { weekday: "short", day: "numeric", month: "long", year: "numeric" },
  numerique: { day: "2-digit", month: "2-digit", year: "numeric" },
  jourMois: { day: "numeric", month: "long" },
  jourMoisCourt: { day: "numeric", month: "short" },
};

/**
 * Le jour de `ms` dans `fuseau` : « lundi 14 juillet » (long) · « lun. 14 juil. » (court) ·
 * « lun. 14 juillet 2026 » (complet) · « 14/07/2026 » · « 14 juillet » · « 14 juil. ».
 */
export function formatJour(ms: number, fuseau: string, style: StyleDeJour = "court", locale = "fr"): string {
  return memo(`j|${locale}|${fuseau}|${style}`, () => new Intl.DateTimeFormat(locale, { timeZone: fuseau, ...OPTIONS_DE_JOUR[style] })).format(
    new Date(ms),
  );
}

/** Le même, pour un jour du calendrier (sans instant ni fuseau). */
export function formatJourCalendaire(j: Jour, style: StyleDeJour = "court", locale = "fr"): string {
  return formatJour(Date.UTC(j.annee, j.mois - 1, j.jour, 12), "UTC", style, locale);
}

/** « lundi » · « lun. » · « L » — le nom d'un jour de la semaine (1 = lundi). */
export function nomDuJour(j: JourDeSemaine, style: "long" | "court" | "etroit" = "court", locale = "fr"): string {
  const weekday = style === "etroit" ? "narrow" : style === "long" ? "long" : "short";
  // 1er janvier 2024 : un lundi.
  return memo(`nj|${locale}|${weekday}`, () => new Intl.DateTimeFormat(locale, { timeZone: "UTC", weekday })).format(
    new Date(Date.UTC(2024, 0, j, 12)),
  );
}

/** « novembre 2026 » — le titre d'un mois. */
export function libelleDuMois(m: Mois, locale = "fr"): string {
  return memo(`m|${locale}`, () => new Intl.DateTimeFormat(locale, { timeZone: "UTC", month: "long", year: "numeric" })).format(
    new Date(Date.UTC(m.annee, m.mois - 1, 15, 12)),
  );
}

/** « novembre » · « nov. » — le nom d'un mois (1 = janvier). */
export function nomDuMois(mois: number, style: "long" | "court" = "long", locale = "fr"): string {
  return memo(`nm|${locale}|${style}`, () => new Intl.DateTimeFormat(locale, { timeZone: "UTC", month: style === "long" ? "long" : "short" })).format(
    new Date(Date.UTC(2024, mois - 1, 15, 12)),
  );
}

export interface OptionsDuree {
  locale?: string;
  /** Au-delà de 24 h, compter en jours (« 3 j 4 h ») — défaut `true`. `false` pour un total d'heures (« 37 h 30 min »). */
  jours?: boolean;
  /** Montrer les secondes sous la minute (« 42 s ») au lieu de « < 1 min ». */
  secondes?: boolean;
}

/**
 * Une durée en mots courts : « 45 min », « 2 h 45 min », « 2 h », « 3 j 4 h », « < 1 min ».
 * Négative : zéro. Deux unités au plus — personne ne lit « 3 j 4 h 12 min ».
 */
export function formatDuree(ms: number, options: OptionsDuree = {}): string {
  const { locale = "fr", jours = true, secondes = false } = options;
  const total = Math.max(0, ms);
  if (total < MINUTE) {
    if (secondes) return unite(locale, "second", Math.floor(total / 1000));
    return `< ${unite(locale, "minute", 1)}`;
  }
  const minutes = Math.floor(total / MINUTE);
  if (jours && total >= JOUR) {
    const j = Math.floor(minutes / 1440);
    const h = Math.floor((minutes % 1440) / 60);
    return h > 0 ? `${unite(locale, "day", j)} ${unite(locale, "hour", h)}` : unite(locale, "day", j);
  }
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return unite(locale, "minute", m);
  return m > 0 ? `${unite(locale, "hour", h)} ${unite(locale, "minute", m)}` : unite(locale, "hour", h);
}

/** Un chronomètre : « 2:45:09 », « 05:09 » sous l'heure. Négatif : zéro. */
export function formatChrono(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const mmss = `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  return h > 0 ? `${h}:${mmss}` : mmss;
}

/**
 * « il y a 12 min », « dans 3 h », « hier », « maintenant » — l'écart entre `ms` et `maintenant`,
 * dans l'unité qui se lit (minutes sous l'heure, heures sous le jour, jours sous le mois…).
 */
export function formatRelatif(ms: number, maintenant: number, locale = "fr"): string {
  const rtf = memo(`r|${locale}`, () => new Intl.RelativeTimeFormat(locale, { numeric: "auto", style: "short" }));
  const ecart = ms - maintenant;
  const a = Math.abs(ecart);
  const signe = Math.sign(ecart) || 1;
  if (a < 45_000) return rtf.format(0, "second");
  if (a < HEURE) return rtf.format(signe * Math.max(1, Math.round(a / MINUTE)), "minute");
  if (a < JOUR) return rtf.format(signe * Math.round(a / HEURE), "hour");
  if (a < 30 * JOUR) return rtf.format(signe * Math.round(a / JOUR), "day");
  if (a < 365 * JOUR) return rtf.format(signe * Math.round(a / (30 * JOUR)), "month");
  return rtf.format(signe * Math.round(a / (365 * JOUR)), "year");
}

/**
 * « UTC+2 », « UTC−3 », « UTC+5:30 » — l'avance de `fuseau` sur UTC à l'instant `ms`.
 * `long` : « UTC+02:00 ». Le signe moins est le vrai (U+2212), pas un trait d'union.
 */
export function formatDecalage(fuseau: string, ms: number = Date.now(), long = false): string {
  const d = decalageDuFuseau(ms, fuseau);
  const signe = d < 0 ? "−" : "+";
  const a = Math.abs(d);
  const h = Math.floor(a / 60);
  const m = a % 60;
  if (long) return `UTC${signe}${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  return `UTC${signe}${h}${m ? `:${String(m).padStart(2, "0")}` : ""}`;
}

/**
 * « aujourd'hui », « hier », « demain », sinon « lundi 14 juillet » — le nom d'un jour vu depuis
 * `aujourdhui` (les deux sont des jours du calendrier, déjà lus dans le bon fuseau).
 */
export function libelleDuJourRelatif(j: Jour, aujourdhui: Jour, locale = "fr"): string {
  const ecart = Math.round((Date.UTC(j.annee, j.mois - 1, j.jour) - Date.UTC(aujourdhui.annee, aujourdhui.mois - 1, aujourdhui.jour)) / JOUR);
  if (Math.abs(ecart) <= 1) {
    return memo(`rj|${locale}`, () => new Intl.RelativeTimeFormat(locale, { numeric: "auto" })).format(ecart, "day");
  }
  return formatJourCalendaire(j, "long", locale);
}
